import styled from '@emotion/styled';
import { withTheme } from '@emotion/react';
import React from 'react';
import { TestId } from '@components/test-ids';
import { Theme, ThemeProps } from '@theme/base';
import { useI18NextContext } from '@components/hooks';
import { CMSTravelGuides, TravelGuide, Entry, TripTypes } from '@model/contentful';
import { boxShadowMixins } from '@styles/mixins';
import { Icon } from '@components/common/generic-icon';
import { GuideLink } from '@components/common/next-link';
import { withIsServer } from '@components/hoc';
import { sampleSize } from 'lodash';
import { Page } from '@model/common';

/* ***************** *
 *       Types       *
 * ***************** */
export interface TravelGuideSideBarProps {
  theme?: Theme;
  testId?: string;
  cmsTravelGuides: CMSTravelGuides;
  currentGuidePath: string;
  path: string;
  tripType?: TripTypes;
  isServer?: boolean;
}

/* ***************** *
 *       Styles      *
 * ***************** */
const Container = styled.div(({ theme }: ThemeProps) => ({
  border: `1px solid ${theme.custom.colors.group10.light}`,
  borderRadius: 16,
  boxShadow: boxShadowMixins(theme.custom.colors.group10.base).default,
  overflow: 'hidden'
}));

const TitleRow = styled.h5(({ theme }: ThemeProps) => ({
  display: 'block',
  backgroundColor: theme.custom.colors.group10.lighter,
  padding: theme.custom.spacing.large,
  margin: 0
}));

const LinkRow = styled.span(({ theme }: ThemeProps) => ({
  display: 'flex',
  justifyContent: 'space-between',
  color: theme.custom.colors.group4.base,
  cursor: 'pointer',
  padding: theme.custom.spacing.large,
  ...(theme.custom.typography.paragraph as any),

  ['&:hover, &:hover i']: {
    color: theme.custom.colors.group1.base
  },

  ['i']: {
    fontSize: '1.5rem',
    lineHeight: '2rem',
    color: theme.custom.colors.group4.base
  }
}));

/* ********************************* *
 *    TravelGuideSideBarComponent    *
 * ********************************* */

export const TravelGuideSideBarComponent = (props: TravelGuideSideBarProps) => {
  const { cmsTravelGuides, currentGuidePath, path, tripType, isServer } = props;

  if (isServer) {
    return null;
  }

  const t: any = useI18NextContext();

  const otherTravelGuides = cmsTravelGuides.filter(
    (travelGuide: Entry<TravelGuide>) => travelGuide.fields.path !== currentGuidePath
  );

  if (!otherTravelGuides.length) {
    return null;
  }

  const randomGuides: Array<Entry<TravelGuide>> = sampleSize(otherTravelGuides, 5);

  /* *** RENDERERS *** */
  const renderSideBarLinks = () =>
    randomGuides.map((travelGuide: Entry<TravelGuide>) => (
      <GuideLink
        key={travelGuide.fields.path}
        path={path}
        guidePath={travelGuide.fields.path}
        tripType={tripType}
        guidePage={Page.TRAVEL_GUIDES}
      >
        <LinkRow>
          {travelGuide.fields.title}
          <Icon name={'mhi-chevron-right'} />
        </LinkRow>
      </GuideLink>
    ));

  return (
    <Container data-testid={props.testId || TestId.guides.sideBar}>
      <TitleRow>{t('travel-guide__sidebar-title--similar-articles')}</TitleRow>
      {renderSideBarLinks()}
    </Container>
  );
};

export const TravelGuideSideBar = withTheme(withIsServer(TravelGuideSideBarComponent));
